/**
 * Stream A — A19: CRM Contact Timeline (real API)
 *
 * Wired to GET /crm/intelligence/contacts/:id/timeline.
 * No mocks. No useApi. Direct apiFetch.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";

export type TimelineEntryType = "email" | "sms" | "whatsapp" | "instagram" | "messenger" | "call" | "meeting" | "booking" | "deal" | "form" | "page" | "note" | "task" | "webinar" | "ai_summary" | "system";

export interface TimelineEntry {
  id: string;
  type: TimelineEntryType;
  channel?: string;
  threadId?: string;
  title: string;
  body?: string;
  direction?: "inbound" | "outbound";
  actorName?: string;
  metadata?: Record<string, unknown>;
  details?: Array<{ label: string; value: string }>;
  actionable?: { label: string; href: string };
  timestamp: string;
  isAiGenerated?: boolean;
}

export function useCrmTimeline(contactId: string | null) {
  const [data, setData] = useState<TimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = useCallback(async () => {
    if (!contactId) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: TimelineEntry[] }>(
        `/crm/intelligence/contacts/${contactId}/timeline`,
      );
      setData(res.data);
    } catch {
      setData([]);
      setError("Failed to load timeline");
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  return { data, isLoading, error, refetch: fetchTimeline };
}

export function useCrmSummarize(contactId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summarize = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: { summary: string; aiGenerated: boolean } }>(
        `/crm/intelligence/contacts/${contactId}/summarize`,
        { method: "POST" },
      );
      return res.data;
    } catch {
      setError("Failed to generate summary");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  return { summarize, isLoading, error };
}

export function useCrmStageSuggestion(contactId: string) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestion = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ data: Record<string, unknown> }>(
        `/crm/intelligence/contacts/${contactId}/stage-suggestion`,
      );
      setData(res.data);
      return res.data;
    } catch {
      setError("Failed to load stage suggestion");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  return { data, isLoading, error, fetchSuggestion };
}
